import * as React from "react";
import { styled, useTheme, Theme, CSSObject } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import CssBaseline from "@mui/material/CssBaseline";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Logo from "../../assets/LogoSemFundo.png";
import userImg from "../../assets/userMenu.svg";
import Style from "../Home/home.module.scss";
import LogoutIcon from "@mui/icons-material/Logout";
import { MenuItem, useMediaQuery } from "@mui/material";
import { Link, Outlet, useNavigate } from "react-router-dom";
import { Popover } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import RequestPageIcon from "@mui/icons-material/RequestPage";
import InventoryIcon from "@mui/icons-material/Inventory";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
const drawerWidth = 240;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
  isMobile?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open" && prop !== "isMobile",
})<AppBarProps>(({ theme, open, isMobile }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  // No mobile o AppBar ocupa 100% (drawer é overlay).
  ...(!isMobile &&
    open && {
      marginLeft: drawerWidth,
      width: `calc(100% - ${drawerWidth}px)`,
      transition: theme.transitions.create(["width", "margin"], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
    }),
}));

const DesktopDrawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

const navItems = [
  {
    key: "Pedidos",
    to: "/app/pedidos",
    label: "Pedidos",
    icon: <RequestPageIcon fontSize="large" sx={{ color: "#9D7063" }} />,
  },
  {
    key: "Clientes",
    to: "/app/clientes",
    label: "Clientes",
    icon: <PeopleAltIcon fontSize="large" sx={{ color: "#9D7063" }} />,
  },
  {
    key: "Estoque",
    to: "/app/estoque",
    label: "Estoque",
    icon: <InventoryIcon fontSize="large" sx={{ color: "#9D7063" }} />,
  },
  {
    key: "Negocio",
    to: "/app/negocio",
    label: "Negócio",
    icon: <BusinessCenterIcon fontSize="large" sx={{ color: "#9D7063" }} />,
  },
  {
    key: "Cadastros",
    to: "/app/cadastros",
    label: "Cadastros",
    icon: <AddCircleIcon fontSize="large" sx={{ color: "#9D7063" }} />,
  },
];

export default function AppHome() {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [open, setOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const openPopover = Boolean(anchorEl);
  const popoverId = openPopover ? "popover-actions" : undefined;

  // Fecha o drawer ao mudar para mobile e evita estado "expandido" do desktop.
  React.useEffect(() => {
    if (isMobile) {
      setOpen(false);
    }
  }, [isMobile]);

  const handleDrawerOpen = () => setOpen(true);
  const handleDrawerClose = () => setOpen(false);

  const handleNavClick = () => {
    if (isMobile) {
      setOpen(false);
    }
  };

  const openUserActions = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => setAnchorEl(null);

  const handleLogoutAction = () => {
    sessionStorage.clear();
    navigate("/");
  };

  const drawerContent = (
    <>
      <DrawerHeader>
        <IconButton onClick={handleDrawerClose} aria-label="fechar menu">
          {theme.direction === "rtl" ? (
            <ChevronRightIcon />
          ) : (
            <ChevronLeftIcon />
          )}
        </IconButton>
      </DrawerHeader>
      <Divider />
      <List>
        {navItems.map((item) => (
          <ListItem
            key={item.key}
            component={Link}
            to={item.to}
            disablePadding
            onClick={handleNavClick}
            sx={{
              "&:hover": {
                backgroundColor: "#d2cdd2",
              },
            }}
          >
            <ListItemButton>
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} sx={{ color: "black" }} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider />
    </>
  );

  const paperSx = {
    backgroundColor: "#F7F2FA",
  };

  return (
    <>
      <Box sx={{ display: "flex", minHeight: "100vh" }}>
        <CssBaseline />
        <AppBar
          position="fixed"
          open={open}
          isMobile={isMobile}
          style={{
            backgroundImage:
              "radial-gradient(circle at left, transparent 85%, rgba(242, 90, 36, 0.5)), linear-gradient(to right, rgba(0, 0, 0, 0.5), rgba(255, 255, 255, 0.5))",
            backgroundColor: "#000000",
          }}
        >
          <Toolbar
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              minHeight: { xs: 56, sm: 64 },
              px: { xs: 1, sm: 2 },
            }}
          >
            <IconButton
              color="inherit"
              aria-label="abrir menu"
              onClick={handleDrawerOpen}
              edge="start"
              sx={{
                // No desktop, esconde o hamburger quando o drawer já está aberto.
                ...(!isMobile && open && { visibility: "hidden" }),
              }}
            >
              <MenuIcon fontSize="large" />
            </IconButton>

            <Box
              sx={{
                flex: 1,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <img src={Logo} alt="Logo WRPneus" className={Style.img} />
            </Box>

            <Box className={Style.userMenu}>
              <img
                src={userImg}
                alt="User Menu"
                onClick={(event) => openUserActions(event)}
              />
            </Box>
          </Toolbar>
        </AppBar>

        {isMobile ? (
          <MuiDrawer
            variant="temporary"
            open={open}
            onClose={handleDrawerClose}
            ModalProps={{ keepMounted: true }}
            sx={{
              "& .MuiDrawer-paper": {
                width: drawerWidth,
                boxSizing: "border-box",
                ...paperSx,
              },
            }}
          >
            {drawerContent}
          </MuiDrawer>
        ) : (
          <DesktopDrawer
            variant="permanent"
            open={open}
            sx={{
              ".MuiPaper-root": paperSx,
            }}
          >
            {drawerContent}
          </DesktopDrawer>
        )}

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            width: { xs: "100%", md: "auto" },
            minWidth: 0,
            p: { xs: 1, sm: 2 },
            pt: 0,
          }}
        >
          <DrawerHeader />
          <Outlet />
        </Box>
      </Box>

      <Popover
        id={popoverId}
        open={openPopover}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={handleClosePopover}>
          <VisibilityIcon style={{ marginRight: "8px" }} />
          Visualizar
        </MenuItem>
        <MenuItem onClick={handleClosePopover}>
          <EditIcon style={{ marginRight: "8px" }} />
          Editar
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogoutAction}>
          <LogoutIcon style={{ marginRight: "8px" }} />
          Sair
        </MenuItem>
      </Popover>
    </>
  );
}
